import { useMemo, useRef } from "react";
import { AgentCursor } from "@/components/cursors/agent-cursor";
import { canDismissCursorTerminalStatus } from "@/components/cursors/agent-cursor-terminal-status";
import { agentCursorDisplayStatus } from "@/components/cursors/agent-cursor-display-status";
import {
  asksDesktopApproval,
  desktopApprovalPoint,
  shouldSuppressFrameOwnedCursorStatus,
} from "@/components/cursors/agent-cursor-intent";
import { cursorBubbleExclusions } from "@/components/cursors/cursor-bubble-exclusions";
import { useRuntimeCursorPill } from "@/components/cursors/use-runtime-cursor-pill";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { DEFAULT_TRAVEL_DURATION_MS } from "@/components/cursors/use-cursor-animation";
import { FLUID_POINTER_DAMPING_SECONDS } from "@/components/cursors/cursor-animation-geometry";
import { useCanvasStore } from "@/stores/canvas-store";
import { notifyCursorAnimationComplete } from "@/lib/cursor-animation-completion";
import { cursorTargetIsOnDisplay } from "@/lib/agent-annotation";
import { cn } from "@/lib/cn";
import { cursorRunIdentity } from "@/lib/run-surface-owner";
import { AgentCursorIdleGreeting } from "./agent-cursor-idle-greeting";
import {
  COMPACT_CURSOR_POINTER_OFFSET,
  getCompactCursorState,
} from "./compact-agent-cursor-state";
import { resolveCursorConversationId } from "./cursor-conversation-id";
import { DesktopAgentAnnotationLayer } from "./desktop-agent-annotation-layer";
import { DesktopFieldEditCues } from "./desktop-field-edit-cues";
import { useActiveDisplay } from "./use-active-display";
import { useDampedFollow } from "./use-damped-follow";
import { runSurface, useRunSurfaceContext } from "./run-surface-context";
import { useWindowOrigin } from "./use-window-origin";
import { prefersReducedMotion } from "@/lib/reveal-motion";

const DISPLAY_HANDOFF_INSET = 36;

export function AgentCursorCompact(props: {
  visible: boolean;
  pointingOnly?: boolean;
}) {
  return (
    <>
      <DesktopAgentAnnotationLayer />
      <DesktopFieldEditCues />
      <CompactCursorSurface {...props} />
      <AgentCursorIdleGreeting visible={props.visible} />
    </>
  );
}

function CompactCursorSurface({
  visible,
  pointingOnly = false,
}: {
  visible: boolean;
  pointingOnly?: boolean;
}) {
  const cursorX = useActiveDisplay((state) => state.cursorX);
  const cursorY = useActiveDisplay((state) => state.cursorY);
  const surfaceContext = useRunSurfaceContext();
  const {
    activeDisplayId,
    activeGuideId,
    agentCursors,
    contextualExecutionIds,
    projectId,
    executions,
    agentSessions,
    visibleConversations,
    displayId: myDisplayId,
  } = surfaceContext;
  const winOrigin = useWindowOrigin();
  const compactState = useMemo(
    () =>
      getCompactCursorState(agentCursors, {
        pointingOnly,
        projectId,
        displayId: myDisplayId,
        activeDisplayId,
        visibleConversations,
        activeGuideId,
        contextualExecutionIds,
        conversationIdForCursor: (cursor) =>
          resolveCursorConversationId(cursor, executions, agentSessions),
      }),
    [
      agentCursors,
      contextualExecutionIds,
      pointingOnly,
      projectId,
      myDisplayId,
      activeDisplayId,
      visibleConversations,
      activeGuideId,
      executions,
      agentSessions,
    ],
  );
  const current = compactState.cursor;
  // A desktop approval points at its target while it asks, never at the mouse.
  const approvalPoint = current ? desktopApprovalPoint(current) : null;
  const pointed =
    current?.pointTarget ?? (approvalPoint && { ...approvalPoint, seq: 0 });
  const currentPointTarget =
    pointed && (myDisplayId === null || pointed.displayId === myDisplayId)
      ? pointed
      : null;
  // A visible conversation frame, or the tray card a docked cursor hands its
  // run to, already shows this run: the cursor stays for its point, drawing or
  // desktop work, without the status pill or bubble.
  const frameOwnsStatus =
    !!current &&
    shouldSuppressFrameOwnedCursorStatus(
      current,
      compactState.frameOwned || pointingOnly,
    );
  // Undocked, a pointing cursor that is its run's one surface asks its
  // question in full beside the arrow; docked or frame-owned keeps the badge.
  const ownsPointedQuestion =
    !!currentPointTarget &&
    !!current?.question &&
    runSurface(
      cursorRunIdentity(
        current,
        resolveCursorConversationId(current, executions, agentSessions),
      ),
      surfaceContext,
      current.question.awaitingInputId,
    ) === "cursor";
  const runtimePill = useRuntimeCursorPill({
    current:
      (currentPointTarget && !ownsPointedQuestion) || frameOwnsStatus
        ? null
        : current,
    visible,
    origin: winOrigin,
    displayId: myDisplayId ?? activeDisplayId ?? undefined,
    exclusions: current
      ? cursorBubbleExclusions(current, winOrigin, {
          width: window.innerWidth,
          height: window.innerHeight,
        })
      : undefined,
  });
  const cursor =
    currentPointTarget || frameOwnsStatus ? current : runtimePill.cursor;
  const contextualOwned =
    !!cursor?.executionId &&
    contextualExecutionIds.includes(cursor.executionId);
  const questionToast = runtimePill.questionToast;
  const follow = useDampedFollow(FLUID_POINTER_DAMPING_SECONDS);
  const lockRef = useRef<{ agentId: string; x: number; y: number } | null>(
    null,
  );

  if (
    contextualOwned ||
    (!visible && !runtimePill.cursor) ||
    !winOrigin.ready ||
    !cursor
  ) {
    if (!runtimePill.cursor) {
      lockRef.current = null;
    }
    follow.setTarget(null);
    return null;
  }
  if (!cursorTargetIsOnDisplay(cursor, myDisplayId)) {
    follow.setTarget(null);
    return null;
  }
  const pointTarget = currentPointTarget;
  // The pill keeps an answered question through its "Answer sent" confirm;
  // without a local work toast for the question the badge stays.
  const pointedQuestionPill =
    !!pointTarget &&
    !!runtimePill.cursor?.question &&
    (!ownsPointedQuestion || !!questionToast);
  const displayStatus = agentCursorDisplayStatus(cursor, true);
  const hasExpandableReply = !!(
    displayStatus.contentFull &&
    displayStatus.content &&
    displayStatus.contentFull.length > displayStatus.content.length
  );
  const canDismiss = canDismissCursorTerminalStatus(cursor, activeGuideId);
  const hasInteractiveContent =
    pointTarget && !pointedQuestionPill
      ? !!displayStatus.question || hasExpandableReply || canDismiss
      : runtimePill.interactive;
  // Only an active pointing cue owns a screen target. Streaming tool status
  // returns beside the user's pointer as soon as that cue clears.
  const desiredPosition = pointTarget
    ? { x: pointTarget.globalX, y: pointTarget.globalY }
    : {
        x: cursorX + COMPACT_CURSOR_POINTER_OFFSET,
        y: cursorY + COMPACT_CURSOR_POINTER_OFFSET,
      };
  const shouldLock =
    !!pointTarget ||
    !!cursor.frozen ||
    !!cursor.error ||
    (pointTarget && !pointedQuestionPill
      ? hasInteractiveContent
      : runtimePill.freezePosition) ||
    !!(cursor.question && questionToast) ||
    !current;
  if (shouldLock) {
    if (pointTarget) {
      lockRef.current = { agentId: cursor.agentId, ...desiredPosition };
    } else if (!lockRef.current || lockRef.current.agentId !== cursor.agentId) {
      lockRef.current = {
        agentId: cursor.agentId,
        ...(follow.getPosition() ?? desiredPosition),
      };
    }
  } else {
    lockRef.current = null;
  }
  const renderedPosition = lockRef.current ?? {
    agentId: cursor.agentId,
    ...desiredPosition,
  };
  follow.setTarget({
    x: renderedPosition.x,
    y: renderedPosition.y,
    initialPosition:
      pointTarget && cursor.presentationDeliveryId
        ? {
            x: cursorX + COMPACT_CURSOR_POINTER_OFFSET,
            y: cursorY + COMPACT_CURSOR_POINTER_OFFSET,
          }
        : undefined,
    snap:
      !!pointTarget &&
      ((pointTarget.travelDurationMs ?? DEFAULT_TRAVEL_DURATION_MS) === 0 ||
        prefersReducedMotion()),
    settleKey: pointTarget?.completionToken,
    onSettled: notifyCursorAnimationComplete,
    // Pointer follow must stay damped: restarting a tween on every 30Hz
    // pointer sample barely moves the pill until the pointer stops.
    durationMs: pointTarget
      ? (pointTarget.travelDurationMs ?? DEFAULT_TRAVEL_DURATION_MS)
      : undefined,
  });
  const rawLeft = renderedPosition.x - winOrigin.x;
  const rawTop = renderedPosition.y - winOrigin.y;
  const hasReplyStatus = !!(
    displayStatus.content ||
    displayStatus.contentFull ||
    displayStatus.error ||
    cursor.frozen ||
    asksDesktopApproval(cursor)
  );
  const suppressStatus =
    frameOwnsStatus ||
    (!hasReplyStatus &&
      (rawLeft < DISPLAY_HANDOFF_INSET ||
        rawTop < DISPLAY_HANDOFF_INSET ||
        rawLeft > window.innerWidth - DISPLAY_HANDOFF_INSET ||
        rawTop > window.innerHeight - DISPLAY_HANDOFF_INSET));
  const bubbleExclusions = cursorBubbleExclusions(cursor, winOrigin, {
    width: window.innerWidth,
    height: window.innerHeight,
  });
  if (compactState.collapsed && !pointTarget) {
    return (
      <div
        ref={follow.elementRef}
        data-testid="desktop-agent-cursor-count"
        {...surfaceRoleAttributes("toolbar", {
          nativeLevel: "island",
          nativeGroup: "agent-cursor",
        })}
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          pointerEvents: "none",
          zIndex: 9999,
        }}
        className={cn(
          surfaceRoleClassName("toolbar"),
          "denker-compact-cursor-count grid h-7 min-w-7 place-items-center border px-2 text-[12px] font-semibold shadow-lg",
        )}
      >
        {compactState.count}
      </div>
    );
  }
  return (
    <div
      ref={follow.elementRef}
      data-interactive={hasInteractiveContent ? "true" : undefined}
      data-presentation-delivery-id={cursor.presentationDeliveryId}
      data-testid="desktop-agent-cursor"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        pointerEvents: hasInteractiveContent ? "auto" : "none",
        zIndex: 9999,
      }}
    >
      <AgentCursor
        key={cursor.agentId}
        agentName={cursor.name}
        agentColor={cursor.color}
        toolCall={displayStatus.toolCall}
        content={displayStatus.content}
        contentFull={displayStatus.contentFull}
        thinking={displayStatus.thinking}
        error={displayStatus.error}
        suppressStatus={suppressStatus}
        questionAwaitingInputId={
          pointTarget && !pointedQuestionPill
            ? displayStatus.question?.awaitingInputId
            : undefined
        }
        onDismiss={
          pointTarget && canDismiss
            ? () =>
                useCanvasStore
                  .getState()
                  .removeAgentCursor(cursor.agentId, { force: true })
            : undefined
        }
        pointing={!!pointTarget}
        className="origin-top-left"
        bubbleExclusions={bubbleExclusions}
        statusPill={
          pointTarget && !pointedQuestionPill
            ? undefined
            : runtimePill.statusPill
        }
      />
    </div>
  );
}
